import { useEffect, useRef } from 'react'
import { useToast } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import type { WbResponse } from '../client/useYjs.js'

/**
 * #1315 B — tell the person when a workbook Add / Remove / Relink sheet they did is refused or fails.
 *
 * Fed by `useYjs`'s per-instance `wbError` (the connection that RECEIVED the reply), never by the `papan:wb-response`
 * window event: that event names no room, and every mounted spreadsheet hears it (workspace tabs stay mounted,
 * `WorkspaceCache` keeps several workspaces), so a toast from it would show once per spreadsheet.
 *
 * Toasts once per frame (the `nonce`, as `useSaveRefusalToast`). `already_in_workbook` / `pointer_not_sheet` and the
 * merge prompt stay WorkspaceView's — nothing here for them.
 */
export function useWorkbookRefusalToast(wbError: { payload: WbResponse; nonce: number } | null): void {
  const toast = useToast()
  const { t } = useTranslation('collab')
  const lastShown = useRef<number | null>(null)

  useEffect(() => {
    if (!wbError) { lastShown.current = null; return }
    if (lastShown.current === wbError.nonce) return
    lastShown.current = wbError.nonce
    const message = workbookRefusalMessage(wbError.payload, t)
    if (message) toast.error(message)
  }, [wbError, toast, t])
}

/** What a workbook-operation error reply shows, or null (another surface owns it). */
export function workbookRefusalMessage(payload: WbResponse, t: (key: string) => string): string | null {
  switch (payload.error) {
    case 'permission_denied':
      return t('workbook.permissionDenied')
    case 'add_sheet_failed':
    case 'remove_sheet_failed':
      return t('workbook.changeFailed')
    // the server's own sentence (the same one the HTTP routes show)
    case 'write_frozen':
    case 'identity_only_tenant':
      return typeof payload.message === 'string' && payload.message ? payload.message : null
    default:
      return null
  }
}
